import React, { useState, useEffect, useRef } from 'react';
import { useParams, Link, useNavigate } from 'react-router-dom';
import { API_BASE_URL } from '../config/api';
import './AdminComponents.css';
import { trackDeletedChat } from '../utils/analytics';

const UserChatHistory = () => {
  const { userId, conversationId } = useParams();
  const navigate = useNavigate();
  const messagesEndRef = useRef(null);
  
  // User and conversation state
  const [user, setUser] = useState(null);
  const [conversations, setConversations] = useState([]);
  const [currentConversation, setCurrentConversation] = useState(null);
  const [selectedConversationId, setSelectedConversationId] = useState(conversationId);
  
  // Messages state
  const [messages, setMessages] = useState([]);
  const [hasMore, setHasMore] = useState(false);
  const [oldestTimestamp, setOldestTimestamp] = useState(null);
  
  // Loading and error states
  const [loading, setLoading] = useState(true);
  const [conversationsLoading, setConversationsLoading] = useState(false);
  const [messagesLoading, setMessagesLoading] = useState(false);
  const [error, setError] = useState(null);
  
  // UI state
  const [sidebarOpen, setSidebarOpen] = useState(true);
  const [includeDeleted, setIncludeDeleted] = useState(() => {
    const urlParams = new URLSearchParams(window.location.search);
    return urlParams.get('includeDeleted') === 'true';
  });

  useEffect(() => {
    // Check if we're on mobile and redirect to mobile view
    const isMobile = window.innerWidth <= 768;
    if (isMobile) {
      navigate(`/admin/users/${userId}/conversations${includeDeleted ? '?includeDeleted=true' : ''}`, { replace: true });
      return;
    }
    
    loadUserAndConversations();
  }, [userId, navigate, includeDeleted]);

  // Re-load conversations when includeDeleted changes
  useEffect(() => {
    if (user) { // Only reload if user is already loaded
      loadUserConversations();
      // Also reload current conversation messages if one is selected
      if (selectedConversationId) {
        loadMessages(selectedConversationId, true);
      }
    }
  }, [includeDeleted]);

  useEffect(() => {
    if (selectedConversationId && selectedConversationId !== conversationId) {
      navigate(`/admin/users/${userId}/chats/${selectedConversationId}`, { replace: true });
    }
  }, [selectedConversationId, conversationId, userId, navigate]);

  useEffect(() => {
    if (conversationId && conversationId !== selectedConversationId) {
      setSelectedConversationId(conversationId);
      loadConversationDetails(conversationId);
    }
  }, [conversationId]);

  useEffect(() => {
    scrollToBottom();
  }, [messages]);

  const getAuthHeaders = () => {
    const token = document.cookie
      .split('; ')
      .find(row => row.startsWith('auth_token='))
      ?.split('=')[1];
    return token ? { 'Authorization': `Bearer ${token}` } : {};
  };

  const scrollToBottom = () => {
    messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' });
  };

  const loadUserAndConversations = async () => {
    try {
      setLoading(true);
      setError(null);

      // Load user details
      const userResponse = await fetch(`${API_BASE_URL}/admin/users/${userId}`, {
        headers: getAuthHeaders()
      });

      if (!userResponse.ok) {
        throw new Error(`Failed to load user: ${userResponse.statusText}`);
      }

      const userData = await userResponse.json();
      setUser(userData.user);

      // Load user's conversations
      await loadUserConversations();

      // If we have a specific conversation ID, load it
      if (conversationId) {
        await loadConversationDetails(conversationId);
      }

    } catch (err) {
      console.error('Error loading user and conversations:', err);
      setError(err.message);
    } finally {
      setLoading(false);
    }
  };

  const loadUserConversations = async () => {
    try {
      setConversationsLoading(true);
      console.log('UserChatHistory: loadUserConversations called with includeDeleted state:', includeDeleted);
      
      const response = await fetch(`${API_BASE_URL}/admin/users/${userId}/conversations?limit=100&includeDeleted=${includeDeleted}`, {
        headers: getAuthHeaders()
      });

      if (!response.ok) {
        throw new Error(`Failed to load conversations: ${response.statusText}`);
      }

      const data = await response.json();
      console.log('UserChatHistory: received conversations data:', {
        totalConversations: data.conversations?.length || 0,
        deletedCount: data.deletedCount || 0,
        includeDeleted: data.includeDeleted
      });
      setConversations(data.conversations || []);

    } catch (err) {
      console.error('Error loading conversations:', err);
      setError(err.message);
    } finally {
      setConversationsLoading(false);
    }
  };

  const loadConversationDetails = async (convId) => {
    try {
      // Load conversation details
      const conversationResponse = await fetch(`${API_BASE_URL}/admin/conversations/${convId}`, {
        headers: getAuthHeaders()
      });

      if (!conversationResponse.ok) {
        throw new Error(`Failed to load conversation: ${conversationResponse.statusText}`);
      }

      const conversationData = await conversationResponse.json();
      setCurrentConversation(conversationData.conversation);

      // Load messages for this conversation
      await loadMessages(convId, true);

      // Log admin action (backend already logs the API calls, this is for additional tracking)
      await logAdminAction('view_conversation_details', {
        conversationId: convId,
        conversationTitle: conversationData.conversation?.title
      });

    } catch (err) {
      console.error('Error loading conversation details:', err);
      setError(err.message);
    }
  };

  const loadMessages = async (convId, reset = false) => {
    if (!convId) return;
    
    try {
      setMessagesLoading(true);

      const params = new URLSearchParams({ limit: '50', includeDeleted: includeDeleted.toString() });
      if (!reset && oldestTimestamp) {
        params.append('before', oldestTimestamp.toString());
      }

      console.log('UserChatHistory: loadMessages called with includeDeleted:', includeDeleted);
      const response = await fetch(`${API_BASE_URL}/admin/conversations/${convId}/messages?${params}`, {
        headers: getAuthHeaders()
      });

      if (!response.ok) {
        throw new Error(`Failed to load messages: ${response.statusText}`);
      }

      const data = await response.json();
      console.log('UserChatHistory: received messages data:', {
        totalMessages: data.messages?.length || 0,
        deletedMessageCount: data.deletedMessageCount || 0,
        includeDeleted: data.includeDeleted
      });
      
      if (reset) {
        setMessages(data.messages || []);
      } else {
        setMessages(prev => [...(data.messages || []), ...prev]);
      }
      
      setHasMore(data.hasMore || false);
      setOldestTimestamp(data.oldestTimestamp || null);

    } catch (err) {
      console.error('Error loading messages:', err);
      setError(err.message);
    } finally {
      setMessagesLoading(false);
    }
  };

  const setShowDeleted = (showDeleted) => {
    console.log('UserChatHistory: setShowDeleted called with:', showDeleted);
    setIncludeDeleted(showDeleted);
    
    // Update URL to reflect the change
    const url = new URL(window.location);
    if (showDeleted) {
      url.searchParams.set('includeDeleted', 'true');
    } else {
      url.searchParams.delete('includeDeleted');
    }
    window.history.replaceState({}, '', url);
    
    // Note: conversations will be reloaded by the useEffect that watches includeDeleted
  };

  // Audit logging utility
  const logAdminAction = async (action, details = {}) => {
    try {
      // The backend automatically logs admin actions when admin endpoints are called
      // This is just for any additional client-side tracking if needed
      console.log('Admin action:', {
        action,
        userId,
        conversationId: selectedConversationId,
        timestamp: new Date().toISOString(),
        ...details
      });
    } catch (error) {
      console.error('Error logging admin action:', error);
      // Don't throw - audit logging failures shouldn't break functionality
    }
  };

  const switchConversation = (convId) => {
    if (convId !== selectedConversationId) {
      setSelectedConversationId(convId);
      setMessages([]);
      setHasMore(false);
      setOldestTimestamp(null);
      loadConversationDetails(convId);
    }
  };

  const handleDeleteConversation = async (convId = selectedConversationId, permanent = false) => {
    if (!convId) return;
    
    const conversation = conversations.find(c => c.conversationId === convId) || currentConversation;
    const title = conversation?.title || 'this conversation';
    
    // Check if trying to soft delete an already deleted conversation
    if (!permanent && conversation?.deleted) {
      alert('This conversation is already soft deleted. Use hard delete to permanently remove it.');
      return;
    }
    
    let confirmed;
    if (permanent) {
      confirmed = window.confirm(
        `⚠️ PERMANENT DELETION WARNING ⚠️\n\n` +
        `Are you sure you want to PERMANENTLY DELETE "${title}"?\n\n` +
        `This will completely remove the conversation and all messages from the database.\n` +
        `This action CANNOT be undone.`
      );
    } else {
      confirmed = window.confirm(
        `Are you sure you want to soft delete "${title}"?\n\n` +
        `This will hide the conversation from the user but preserve all data.\n` +
        `It can be restored later if needed.`
      );
    }
    
    if (!confirmed) {
      return;
    }

    try {
      const endpoint = permanent 
        ? `${API_BASE_URL}/admin/conversations/${convId}/permanent`
        : `${API_BASE_URL}/admin/conversations/${convId}`;
        
      const response = await fetch(endpoint, {
        method: 'DELETE',
        headers: getAuthHeaders()
      });

      if (!response.ok) {
        const errorData = await response.json();
        throw new Error(errorData.message || `Failed to delete conversation: ${response.statusText}`);
      }

      // Log successful deletion
      await logAdminAction(permanent ? 'permanent_delete_conversation' : 'delete_conversation', {
        conversationId: convId,
        conversationTitle: title,
        userEmail: user?.email,
        deletionType: permanent ? 'permanent' : 'soft'
      });

      // Track chat deletion
      trackDeletedChat();

      // Remove from conversations list
      setConversations(prev => prev.filter(c => c.conversationId !== convId));
      
      // If we deleted the current conversation, clear it and redirect
      if (convId === selectedConversationId) {
        setCurrentConversation(null);
        setMessages([]);
        
        // Navigate to first remaining conversation or back to user detail
        const remainingConversations = conversations.filter(c => c.conversationId !== convId);
        if (remainingConversations.length > 0) {
          navigate(`/admin/users/${userId}/chats/${remainingConversations[0].conversationId}`, { replace: true });
        } else {
          navigate(`/admin/users/${userId}`, { replace: true });
        }
      }

    } catch (err) {
      console.error('Error deleting conversation:', err);
      setError(err.message);
    }
  };

  const exportConversation = (convId = selectedConversationId) => {
    if (!convId || !currentConversation) return;
    
    const exportData = {
      exportedAt: new Date().toISOString(),
      user: {
        userId: user.userId,
        email: user.email
      },
      conversation: {
        conversationId: currentConversation.conversationId,
        title: currentConversation.title,
        createdAt: currentConversation.createdAt,
        lastMessageAt: currentConversation.lastMessageAt,
        model: currentConversation.model
      },
      messages: messages.map(msg => ({
        messageId: msg.messageId,
        role: msg.role,
        content: msg.content,
        timestamp: msg.timestamp,
        model: msg.model
      })),
      metadata: {
        totalMessages: messages.length,
        exportedBy: 'admin',
        exportFormat: 'json'
      }
    };

    const dataStr = JSON.stringify(exportData, null, 2);
    const dataBlob = new Blob([dataStr], { type: 'application/json' });
    const url = URL.createObjectURL(dataBlob);
    
    const link = document.createElement('a');
    link.href = url;
    link.download = `conversation-${convId}-${new Date().toISOString().split('T')[0]}.json`;
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
    URL.revokeObjectURL(url);

    // Log export action
    logAdminAction('export_conversation', {
      conversationId: convId,
      conversationTitle: currentConversation?.title,
      messageCount: messages.length,
      userEmail: user?.email
    });
  };

  const exportAllConversations = () => {
    if (!conversations.length) return;
    
    const exportData = {
      exportedAt: new Date().toISOString(),
      user: {
        userId: user.userId,
        email: user.email
      },
      conversations: conversations.map(conv => ({
        conversationId: conv.conversationId,
        title: conv.title,
        createdAt: conv.createdAt,
        lastMessageAt: conv.lastMessageAt,
        model: conv.model,
        messageCount: conv.messageCount || 0
      })),
      metadata: {
        totalConversations: conversations.length,
        exportedBy: 'admin',
        exportFormat: 'json'
      }
    };

    const dataStr = JSON.stringify(exportData, null, 2);
    const dataBlob = new Blob([dataStr], { type: 'application/json' });
    const url = URL.createObjectURL(dataBlob);
    
    const link = document.createElement('a');
    link.href = url;
    link.download = `user-${userId}-conversations-${new Date().toISOString().split('T')[0]}.json`;
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
    URL.revokeObjectURL(url);

    // Log export action
    logAdminAction('export_all_conversations', {
      userId,
      userEmail: user?.email,
      conversationCount: conversations.length
    });
  };

  const formatTimestamp = (timestamp) => {
    return new Date(timestamp).toLocaleString();
  };

  const getMessageRoleClass = (role) => {
    return role === 'user' ? 'message-user' : 'message-assistant';
  };

  if (loading) {
    return (
      <div className="user-chat-history">
        <div className="loading-state">
          <div className="loading-spinner"></div>
          <p>Loading chat history...</p>
        </div>
      </div>
    );
  }

  if (error && !user) {
    return (
      <div className="user-chat-history">
        <div className="error-state">
          <h3>⚠️ Error Loading Chat History</h3>
          <p>{error}</p>
          <div className="error-actions">
            <button onClick={loadUserAndConversations} className="retry-button">
              🔄 Retry
            </button>
            <Link to={`/admin/users/${userId}`} className="back-button">
              ← Back to User
            </Link>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="user-chat-history">
      {/* Header with admin controls */}
      <div className="chat-history-header">
        <div className="header-info">
          <h2>💬 Chat History</h2>
          <div className="breadcrumb-info">
            <Link to={`/admin/users/${userId}`} className="breadcrumb-link">
              {user?.email}
            </Link>
            {currentConversation && (
              <>
                <span className="breadcrumb-separator">›</span>
                <span>{currentConversation.title || 'Untitled Conversation'}</span>
              </>
            )}
          </div>
        </div>
        
        <div className="header-actions">
          <button
            onClick={() => setSidebarOpen(!sidebarOpen)}
            className="sidebar-toggle-button"
            title={sidebarOpen ? 'Hide conversations' : 'Show conversations'}
          >
            {sidebarOpen ? '◀' : '▶'}
          </button>
          {conversations.length > 0 && (
            <button 
              onClick={exportAllConversations}
              className="export-all-button"
              title="Export all conversations"
            >
              📦 Export All
            </button>
          )}
          {currentConversation && (
            <>
              <button 
                onClick={() => exportConversation()}
                className="export-button"
                title="Export current conversation"
              >
                📥 Export
              </button>
              <div className="delete-buttons-group">
                <button 
                  onClick={() => handleDeleteConversation(selectedConversationId, false)}
                  className={`delete-button soft-delete ${currentConversation?.deleted ? 'disabled' : ''}`}
                  disabled={currentConversation?.deleted}
                  title={currentConversation?.deleted ? "Already soft deleted" : "Soft delete - hide from user but preserve data"}
                >
                  🗑️ Soft Delete
                </button>
                <button 
                  onClick={() => handleDeleteConversation(selectedConversationId, true)}
                  className="delete-button hard-delete"
                  title="Permanent delete - completely remove from database"
                >
                  ⚠️ Hard Delete
                </button>
              </div>
            </>
          )}
          <Link to={`/admin/users/${userId}`} className="back-button">
            ← Back to User
          </Link>
        </div>
      </div>

      {error && (
        <div className="error-message">
          <span>⚠️ {error}</span>
          <button onClick={() => setError(null)}>✕</button>
        </div>
      )}

      <div className={`chat-layout ${sidebarOpen ? 'sidebar-open' : 'sidebar-closed'}`}>
        {/* Conversations Sidebar - Reusing ChatApp structure */}
        <div className={`conversations-sidebar ${sidebarOpen ? 'open' : 'closed'}`}>
          <div className="sidebar-header">
            <div className="sidebar-title">
              <h3>Conversations ({conversations.length})</h3>
              {conversationsLoading && <div className="loading-spinner small"></div>}
            </div>
            <div className="sidebar-controls">
              <label className="checkbox-label">
                <input
                  type="checkbox"
                  checked={includeDeleted}
                  onChange={(e) => setShowDeleted(e.target.checked)}
                />
                <span className="checkbox-text">Show deleted</span>
              </label>
            </div>
          </div>
          
          <div className="conversations-list">
            {conversations.length === 0 ? (
              <div className="empty-conversations">
                <p>No conversations found</p>
              </div>
            ) : (
              conversations
                .sort((a, b) => new Date(b.lastMessageAt || b.createdAt) - new Date(a.lastMessageAt || a.createdAt))
                .map(conversation => (
                  <div
                    key={conversation.conversationId}
                    className={`conversation-item ${conversation.conversationId === selectedConversationId ? 'active' : ''} ${conversation.deleted ? 'deleted' : ''}`}
                    onClick={() => switchConversation(conversation.conversationId)}
                  >
                    <div className="conversation-title">
                      {conversation.title || 'Untitled Conversation'}
                      {conversation.deleted && <span className="deleted-indicator">🗑️</span>}
                    </div>
                    <div className="conversation-meta">
                      <span className="conversation-date">
                        {formatTimestamp(conversation.lastMessageAt || conversation.createdAt)}
                      </span>
                      {conversation.messageCount && (
                        <span className="message-count">
                          {conversation.messageCount} messages
                        </span>
                      )}
                      {conversation.deleted && (
                        <span className="deleted-status">DELETED</span>
                      )}
                    </div>
                    <div className="conversation-actions">
                      <button
                        onClick={(e) => {
                          e.stopPropagation();
                          exportConversation(conversation.conversationId);
                        }}
                        className="conversation-action-button"
                        title="Export conversation"
                      >
                        📥
                      </button>
                      <button
                        onClick={(e) => {
                          e.stopPropagation();
                          handleDeleteConversation(conversation.conversationId, false);
                        }}
                        className={`conversation-action-button delete soft-delete ${conversation.deleted ? 'disabled' : ''}`}
                        disabled={conversation.deleted}
                        title={conversation.deleted ? "Already soft deleted" : "Soft delete conversation"}
                      >
                        🗑️
                      </button>
                      <button
                        onClick={(e) => {
                          e.stopPropagation();
                          handleDeleteConversation(conversation.conversationId, true);
                        }}
                        className="conversation-action-button delete hard-delete"
                        title="Permanently delete conversation"
                      >
                        ⚠️
                      </button>
                    </div>
                  </div>
                ))
            )}
          </div>
        </div>

        {/* Main Chat Area - Reusing ChatApp structure */}
        <div className="chat-main">
          {currentConversation ? (
            <>
              {/* Conversation Info Panel */}
              <div className="conversation-info-panel">
                <div className="conversation-details">
                  <h4>{currentConversation.title || 'Untitled Conversation'}</h4>
                  <div className="conversation-metadata">
                    <span>Created: {formatTimestamp(currentConversation.createdAt)}</span>
                    <span>Last Activity: {formatTimestamp(currentConversation.lastMessageAt)}</span>
                    <span>Messages: {messages.length}</span>
                    {includeDeleted && messages.filter(m => m.deleted).length > 0 && (
                      <span className="deleted-count">
                        🗑️ Deleted: {messages.filter(m => m.deleted).length}
                      </span>
                    )}
                    {currentConversation.model && <span>Model: {currentConversation.model}</span>}
                  </div>
                </div>
              </div>

              {/* Messages Container - Reusing ChatApp structure */}
              <div className="messages-container">
                {hasMore && (
                  <div className="load-more-container">
                    <button 
                      onClick={() => loadMessages(selectedConversationId, false)}
                      disabled={messagesLoading}
                      className="load-more-button"
                    >
                      {messagesLoading ? 'Loading...' : 'Load Earlier Messages'}
                    </button>
                  </div>
                )}

                <div className="messages-list">
                  {messages.length === 0 ? (
                    <div className="empty-messages">
                      <p>No messages in this conversation.</p>
                    </div>
                  ) : (
                    messages.map(message => (
                      <div key={message.messageId} className={`message ${message.role} ${message.deleted ? 'deleted' : ''}`}>
                        <div className="message-header">
                          <span className="message-role">
                            {message.role === 'user' ? '👤 User' : 
                             message.role === 'system' ? '⚙️ System' : '🤖 Assistant'}
                          </span>
                          <span className="message-timestamp">
                            {formatTimestamp(message.timestamp)}
                          </span>
                          {message.model && (
                            <span className="message-model">
                              {message.model}
                            </span>
                          )}
                          {message.deleted && (
                            <span className="deleted-badge">🗑️ DELETED</span>
                          )}
                        </div>
                        <div className="message-content">
                          {message.content}
                        </div>
                        {message.deleted && message.deletedAt && (
                          <div className="message-deleted-info">
                            Deleted: {formatTimestamp(message.deletedAt)}
                          </div>
                        )}
                      </div>
                    ))
                  )}
                </div>
                <div ref={messagesEndRef} />
              </div>
            </>
          ) : (
            <div className="no-conversation-selected">
              <h3>Select a Conversation</h3>
              <p>Choose a conversation from the sidebar to view its messages.</p>
              {conversations.length === 0 && (
                <p>This user has no conversations yet.</p>
              )}
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default UserChatHistory;